import React, { useContext, useEffect, useState } from 'react';
import loadScript from '../../utils/loadScript';
import { getPayInst } from '../../services/paymentService';
import { cardParams, dividerObj, applePayObj, payPalObj, styles, countriesObj } from '../../const/cardData';
import Loader from '../Loader';
import FieldControl from '../InputControls/FieldControl';
import BookerContext from '../../context/bookerContext';
import BookerShortContext from '../../context/bookerShortContext';
import jwtDecode from 'jwt-decode';
import authService from '../../services/authService';
import FetchService from '../../services/fetchService';
import urlService from '../../services/urlService';
import addPayments from '../../utils/addPayments';
import { useValidatedInput } from '../../hooks/useValidatedInput';
import { getPaymentTotalData, saveApplePayPayment, savePayPalPayment } from '../../utils/savePayments';

const Payments = ({ saveCard, setLoading, openCountryField, handleError }) => {
  const context = useContext(BookerContext);
  const { state, updateStep, updateInst, updatePayment, updatePaymentMethodId, updateEditor, updatePreviousStep } =
    context;
  const { payment = {}, termsAndConditions = {}, fare = {}, isWebBooker, editor } = state;
  const { setUpdatePaymentsMenu, setIsCardValid } = useContext(BookerShortContext);

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const [setIsValid, setDoValidateForm, isFormValid, toggleBtnDisability] = useValidatedInput('btn-view', false);
  let inst = null;
  toggleBtnDisability(!(isIframeLoaded && payment?.description));

  useEffect(() => {
    updateCardView();
  }, []);

  useEffect(() => {
    if (!isIframeLoaded) return;

    setIsValid(payment?.description);
    toggleBtnDisability(!payment?.description);
  }, [isIframeLoaded, payment?.description]);

  const updateCardView = () => {
    const url = process.env.REACT_APP_STAGE
      ? 'https://way2i.stage.gocurb.com/public/resources/payment.js'
      : 'https://way2i.gocurb.com/public/resources/payment.js';

    loadScript(url)
      .then(() => {
        createIframeTempl();
      })
      .catch(() => {
        setIsIframeLoaded(false);
        setLoading(false);
        setIsCardValid(false);
        updatePayment({});
        updatePaymentMethodId(null);

        handleError({
          message: "Sorry, this page couldn't be loaded. Please check your internet connection and try again.",
        });
      });
  };

  const createIframeTempl = () => {
    const bindEvent = (element, eventName, eventHandler) => {
      if (element.addEventListener) {
        element.addEventListener(eventName, eventHandler, false);
      } else if (element.attachEvent) {
        element.attachEvent('on' + eventName, eventHandler);
      }
    };

    const callbackHandler = (err, paymentData, isProcessing) => {
      if (err) {
        setIsIframeLoaded(false);
        setLoading(false);
        setIsCardValid(false);
        updatePayment({});
        updatePaymentMethodId(null);
        console.log('Payment is failed ', err);
        return;
      }

      if (paymentData) {
        addPayments({
          inst,
          updatePayment,
          updatePaymentMethodId,
          paymentData,
          setIsIframeLoaded,
          updateStep,
          updateEditor,
          updatePreviousStep,
          state,
          setUpdatePaymentsMenu,
          setIsCardValid,
          setLoading,
        });
        console.log('Payment callback with success');
      }

      if (isProcessing) {
        setLoading(true);
        console.log('Payment in process');
      }
    };

    const paymentContainer = document.getElementsByClassName('payment-card-container')[0];
    if (paymentContainer) {
      paymentContainer.innerHTML = '';
    }

    cardParams['cc-number'].value = payment.card_number;
    cardParams['cc-expiry'].value = payment.exp_date;
    cardParams['cc-zip'].value = payment.zip;
    cardParams['cc-cvv'].value = payment.cvv;

    if (state.country) {
      countriesObj.countries = { [state.country.id]: state.country };
      countriesObj.selectedCountry = state.country;
    }

    const jwt = authService.getDelegationToken();
    if (!jwt) return;

    const user_id = jwtDecode(jwt).sub;
    const config = {
      font: 'Roboto',
      styles,
      dividerObj,
      applePayObj: null,
      payPalObj: null,
      container: 'payment-card-container',
      isScrollIframe: 'no',
      user_id,
      countriesObj,
      url: `${window.location.origin}/${Math.random().toString(36)}`,
    };

    inst = getPayInst(cardParams, config, callbackHandler);
    inst.renderIframeTemplate();

    updateInst(inst);

    bindEvent(window, 'message', e => {
      if (e.data && e.data.isInputChange) {
        const card = inst.collectCardData();
        updatePayment(card);
        setIsValid(true);
        return;
      }

      if (e.data && e.data.isAmount) {
        if (!(!isWebBooker && document.getElementsByClassName('terms-conditions-check')[0].checked)) {
          return;
        }

        const card = inst.collectCardData({ applePay: true });
        saveCard(card);
        saveApplePayPayment(inst, isWebBooker);
        return;
      }

      if (e.data && e.data.isPayPal) {
        if (!(!isWebBooker && document.getElementsByClassName('terms-conditions-check')[0].checked)) {
          return;
        }

        const card = inst.collectCardData({ payPal: true });
        saveCard(card);
        savePayPalPayment(inst, isWebBooker);

        return;
      }

      if (e.data && e.data.isCountryDropdownOpen) {
        const ifr = document.getElementById('addPaymentMethodIframe');
        if (ifr) {
          if ('virtualKeyboard' in navigator) {
            navigator.virtualKeyboard.overlaysContent = true;
          }
          const card = inst.collectCardData();
          updatePayment(card);
        }

        openCountryField(e.data.isCountryDropdownOpen);
        return;
      }

      if (e.data?.source?.includes('react-devtools')) return;

      if (document.getElementById('addPaymentMethodIframe')) {
        FetchService.getWebApiResource(urlService.getWay2IToken())
          .then(response => {
            const data = {
              total: getPaymentTotalData(),
              token: response.token,
            };
            document.getElementById('addPaymentMethodIframe').contentWindow.postMessage(data, '*');
          })
          .catch(() => {
            document.getElementById('addPaymentMethodIframe')?.contentWindow.postMessage('', '*');
          });
      }
    });

    const intervalIframeId = setInterval(() => {
      resetIframeToMaxHeight();
    }, 1000);
    setIntervalId(intervalIframeId);
  };

  const resetIframeToMaxHeight = () => {
    const ifr = document.getElementById('addPaymentMethodIframe');
    if (!ifr) return;

    const frm = ifr.contentWindow.document.getElementById('payment-form');
    if (!frm) return;

    setIsIframeLoaded(true);

    clearInterval(intervalId);
    setIntervalId(null);
  };

  return (
    <div className="form-group-container">
      <div className="title">
        {editor?.isOpenedFromMenu ? 'Add a credit card' : 'Enter a credit card to pay for this ride'}
      </div>
      <div className={'payment-card-container' + (!isIframeLoaded ? ' hidden' : '')} />
      <Loader loading={!isIframeLoaded} />
    </div>
  );
};

export default Payments;
